
// Action: file_editor create /app/frontend/src/components/About.js --file-text "
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Sparkles } from 'lucide-react';

const About = () => {
  const features = [
    {
      icon: Award,
      title: 'Quality Craftsmanship',
      description: '15+ years of excellence in interior design'
    },
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Skilled professionals dedicated to your vision'
    },
    {
      icon: Sparkles,
      title: 'Modern Designs',
      description: 'Contemporary solutions for every space'
    }
  ];

  const services = [
    'Residential & Commercial Interiors',
    'Modular Kitchens & Wardrobes',
    'Glass Partitions & Glass Works',
    'False Ceilings & Custom Solutions'
  ];

  return (
    <section data-testid="about-section" className="py-24 md:py-32 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative h-[500px] md:h-[600px]">
              <img
                src="https://images.pexels.com/photos/8961298/pexels-photo-8961298.jpeg"
                alt="Interior Design Process"
                className="w-full h-full object-cover rounded-sm"
              />
              {/* Accent Box */}
              <div className="absolute -bottom-8 -right-8 bg-[#D4AF37] text-white p-8 rounded-sm">
                <p className="font-playfair text-5xl font-bold">15+</p>
                <p className="font-manrope text-sm tracking-widest uppercase mt-2">
                  Years Experience
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="font-manrope text-sm tracking-widest uppercase text-[#D4AF37] mb-4">
              About Us
            </p>
            
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-6 tracking-tight">
              Crafting Spaces with{' '}
              <span className="italic">Excellence</span>
            </h2>

            <p className="font-manrope text-base md:text-lg text-[#666666] leading-relaxed mb-6">
              At <strong>Shree Ram Interior & Glass Works</strong>, we bring your vision to life through 
              meticulous craftsmanship and innovative design solutions. With over 15 years of experience, 
              we specialize in creating elegant interiors that blend functionality with aesthetic appeal.
            </p>

            <p className="font-manrope text-base md:text-lg text-[#666666] leading-relaxed mb-8">
              Our commitment to quality, attention to detail, and customer satisfaction has made us 
              a trusted name in residential and commercial interior design. From concept to completion, 
              we ensure every project reflects the highest standards of excellence.
            </p>

            {/* Services List */}
            <div className="mb-10">
              <h3 className="font-playfair text-2xl font-semibold text-[#1A1A1A] mb-4">
                Our Services
              </h3>
              <div className="space-y-3">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle size={20} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                    <p className="font-manrope text-[#1A1A1A]">{service}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#D4AF37]/10 rounded-full mb-3">
                    <feature.icon size={24} className="text-[#D4AF37]" />
                  </div>
                  <h4 className="font-manrope font-semibold text-[#1A1A1A] mb-1">
                    {feature.title}
                  </h4>
                  <p className="font-manrope text-sm text-[#666666]">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
// Observation: Create successful: /app/frontend/src/components/About.js